import { useMemo } from 'react';
import { getUnixTime } from '@einhaus/meteoric-helpers';
import type {
    BarDuration,
    ChartPatternDisplayMode,
    ChartPatternDirection,
    ChartPatternKey,
    ChartPatternResolvedEvent,
    ChartPatternResolvedOccurrence,
    ChartPatternResolvedSnapshot,
    ChartPatternState,
    ChartPatternSummary,
    ChartPatternSummaryCounts
} from '@einhaus/project314-types';
import {
    getChartPatternEventsForDisplay,
    getChartPatternHistoricalEventsForDisplay,
    getChartPatternHistoricalOccurrencesForDisplay,
    getChartPatternSnapshotsForDisplay
} from './chartPatternDisplayHelpers.ts';

type ChartPatternPanelProps = {
    chartPatternDisplayMode: ChartPatternDisplayMode;
    chartPatternKeyFilter?: ChartPatternKey | null;
    duration: BarDuration;
    focusedChartPatternOccurrenceId?: number | null;
    historicalEvents: ChartPatternResolvedEvent[];
    historicalOccurrences: ChartPatternResolvedOccurrence[];
    isLoading: boolean;
    onChartPatternKeyFilterChange: (patternKey: ChartPatternKey | null) => void;
    onFocusOccurrence: (occurrenceId: number, targetDisplayMode?: ChartPatternDisplayMode) => void;
    onShowHistoricalOccurrences: () => void;
    onShowPitReplay: () => void;
    onShowAllCurrent: () => void;
    onShowSelected: () => void;
    summary: ChartPatternSummary | null;
};

const MODE_BUTTON_CLASS = 'rounded border px-3 py-1 text-xs font-medium transition-colors disabled:cursor-default disabled:opacity-100';

const DIRECTION_LABELS: Record<ChartPatternDirection, string> = {
    bullish: 'Bullish',
    bearish: 'Bearish',
    neutral: 'Neutral'
};

const DIRECTION_BADGE_CLASS: Record<ChartPatternDirection, string> = {
    bullish: 'border-emerald-500/40 bg-emerald-500/10 text-emerald-300',
    bearish: 'border-rose-500/40 bg-rose-500/10 text-rose-300',
    neutral: 'border-zinc-600 bg-zinc-800 text-zinc-300'
};

const STATE_BADGE_CLASS: Record<ChartPatternState, string> = {
    forming: 'border-sky-500/40 bg-sky-500/10 text-sky-300',
    formed: 'border-amber-500/40 bg-amber-500/10 text-amber-300',
    triggered: 'border-emerald-500/40 bg-emerald-500/10 text-emerald-300',
    successful: 'border-green-600/40 bg-green-600/10 text-green-300',
    failed: 'border-rose-500/40 bg-rose-500/10 text-rose-300',
    expired: 'border-zinc-600 bg-zinc-800 text-zinc-300'
};

const formatPatternLabel = (value: string): string =>
    value
        .replace(/([a-z])([A-Z])/g, '$1 $2')
        .replace(/([A-Z])([A-Z][a-z])/g, '$1 $2')
        .replace(/^./, (character) => character.toUpperCase());

const formatDirectionLabel = (direction: ChartPatternDirection): string => DIRECTION_LABELS[direction];

const formatMetricNumber = (value: number | null | undefined): string => {
    if (value == null) return 'n/a';
    return value.toFixed(2);
};

const formatDistanceLabel = (value: number | null | undefined): string => {
    if (value == null) return 'n/a';
    return `${value.toFixed(1)} bars`;
};

const formatShortDateTime = (value: string): string => {
    const trimmed = value.replace('T', ' ');
    return trimmed.length > 16 ? trimmed.slice(0, 16) : trimmed;
};

const getDirectionBadgeClass = (direction: ChartPatternDirection): string => DIRECTION_BADGE_CLASS[direction];

const getStateBadgeClass = (state: ChartPatternResolvedSnapshot['state']): string => STATE_BADGE_CLASS[state];

const getConfirmationLabel = (direction: ChartPatternDirection): string => {
    return direction === 'bearish' ? 'Breakdown' : 'Breakout';
};

const StatPill = ({ label, value }: { label: string; value: number }) => (
    <div className="rounded border border-zinc-800 bg-zinc-950 px-3 py-2">
        <div className="text-[10px] uppercase tracking-[0.16em] text-zinc-500">{label}</div>
        <div className="mt-1 text-sm font-medium text-zinc-100">{String(value)}</div>
    </div>
);

const SummaryCounts = ({ counts }: { counts: ChartPatternSummaryCounts }) => (
    <div className="grid grid-cols-3 gap-2 md:grid-cols-6">
        <StatPill label="Active" value={counts.active} />
        <StatPill label="Forming" value={counts.forming} />
        <StatPill label="Formed" value={counts.formed} />
        <StatPill label="Triggered" value={counts.triggered} />
        <StatPill label="Failed" value={counts.failed} />
        <StatPill label="Fresh" value={counts.fresh} />
    </div>
);

const buildFilteredSummaryCounts = (snapshots: ChartPatternResolvedSnapshot[]): ChartPatternSummaryCounts => {
    const counts: ChartPatternSummaryCounts = {
        total: snapshots.length,
        active: 0,
        bullish: 0,
        bearish: 0,
        forming: 0,
        formed: 0,
        breakoutReady: 0,
        triggered: 0,
        successful: 0,
        failed: 0,
        expired: 0,
        fresh: 0
    };

    for (const snapshot of snapshots) {
        if (snapshot.direction === 'bullish') counts.bullish += 1;
        if (snapshot.direction === 'bearish') counts.bearish += 1;
        if (snapshot.state !== 'failed' && snapshot.state !== 'expired') counts.active += 1;
        if (snapshot.state === 'forming') counts.forming += 1;
        if (snapshot.state === 'formed') counts.formed += 1;
        if (snapshot.isBreakoutReady) counts.breakoutReady += 1;
        if (snapshot.isTriggered) counts.triggered += 1;
        if (snapshot.state === 'successful') counts.successful += 1;
        if (snapshot.state === 'failed') counts.failed += 1;
        if (snapshot.state === 'expired') counts.expired += 1;
        if (snapshot.isFresh) counts.fresh += 1;
    }

    return counts;
};

const buildFilteredOccurrenceCounts = (occurrences: ChartPatternResolvedOccurrence[]): ChartPatternSummaryCounts => {
    const counts: ChartPatternSummaryCounts = {
        total: occurrences.length,
        active: 0,
        bullish: 0,
        bearish: 0,
        forming: 0,
        formed: 0,
        breakoutReady: 0,
        triggered: 0,
        successful: 0,
        failed: 0,
        expired: 0,
        fresh: 0
    };

    for (const occurrence of occurrences) {
        if (occurrence.direction === 'bullish') counts.bullish += 1;
        if (occurrence.direction === 'bearish') counts.bearish += 1;
        if (occurrence.state !== 'failed' && occurrence.state !== 'expired') counts.active += 1;
        if (occurrence.state === 'forming') counts.forming += 1;
        if (occurrence.state === 'formed') counts.formed += 1;
        if (occurrence.phaseKey === 'breakoutReady' || occurrence.state === 'formed') counts.breakoutReady += 1;
        if (occurrence.state === 'triggered') counts.triggered += 1;
        if (occurrence.state === 'successful') counts.successful += 1;
        if (occurrence.state === 'failed') counts.failed += 1;
        if (occurrence.state === 'expired') counts.expired += 1;
    }

    return counts;
};

const SnapshotCard = ({
    isFocused,
    snapshot,
    onFocus
}: {
    isFocused: boolean;
    snapshot: ChartPatternResolvedSnapshot;
    onFocus: (occurrenceId: number) => void;
}) => (
    <button
        className={`rounded border p-3 text-left transition-colors ${
            isFocused ? 'border-blue-500 bg-blue-500/10' : 'border-zinc-800 bg-zinc-950 hover:border-zinc-600'
        }`}
        type="button"
        onClick={() => onFocus(snapshot.occurrenceId)}
    >
        <div className="flex flex-wrap items-center gap-2">
            <span className="text-sm font-semibold text-zinc-100">{formatPatternLabel(snapshot.patternKey)}</span>
            <span className={`rounded border px-2 py-0.5 text-[11px] ${getDirectionBadgeClass(snapshot.direction)}`}>
                {formatDirectionLabel(snapshot.direction)}
            </span>
            <span className={`rounded border px-2 py-0.5 text-[11px] ${getStateBadgeClass(snapshot.state)}`}>{snapshot.state}</span>
            {snapshot.strictnessTier && (
                <span className="rounded border border-zinc-700 bg-zinc-900 px-2 py-0.5 text-[11px] text-zinc-300">
                    {snapshot.strictnessTier}
                </span>
            )}
        </div>

        <div className="mt-3 grid grid-cols-2 gap-3 text-xs text-zinc-400 md:grid-cols-4">
            <div>
                <div className="uppercase tracking-[0.12em] text-zinc-500">Snapshot</div>
                <div className="mt-1 text-zinc-200">{formatShortDateTime(snapshot.snapshotDateTime)}</div>
            </div>
            <div>
                <div className="uppercase tracking-[0.12em] text-zinc-500">Phase</div>
                <div className="mt-1 text-zinc-200">{snapshot.phaseKey ?? 'n/a'}</div>
            </div>
            <div>
                <div className="uppercase tracking-[0.12em] text-zinc-500">Quality</div>
                <div className="mt-1 text-zinc-200">{formatMetricNumber(snapshot.qualityScore)}</div>
            </div>
            <div>
                <div className="uppercase tracking-[0.12em] text-zinc-500">Last Event</div>
                <div className="mt-1 text-zinc-200">{snapshot.lastEventKey ?? snapshot.lastEventCategory ?? 'n/a'}</div>
            </div>
            <div>
                <div className="uppercase tracking-[0.12em] text-zinc-500">Bars Since Event</div>
                <div className="mt-1 text-zinc-200">{formatDistanceLabel(snapshot.barsSinceLastEvent)}</div>
            </div>
            <div>
                <div className="uppercase tracking-[0.12em] text-zinc-500">{getConfirmationLabel(snapshot.direction)}</div>
                <div className="mt-1 text-zinc-200">{formatMetricNumber(snapshot.breakoutPrice)}</div>
            </div>
            <div>
                <div className="uppercase tracking-[0.12em] text-zinc-500">Stop</div>
                <div className="mt-1 text-zinc-200">{formatMetricNumber(snapshot.stopPrice)}</div>
            </div>
            <div>
                <div className="uppercase tracking-[0.12em] text-zinc-500">Target</div>
                <div className="mt-1 text-zinc-200">{formatMetricNumber(snapshot.target1Price)}</div>
            </div>
        </div>

        {snapshot.defectFlags.length > 0 && (
            <div className="mt-3 flex flex-wrap gap-2">
                {snapshot.defectFlags.slice(0, 4).map((flag) => (
                    <span className="rounded border border-zinc-700 bg-zinc-900 px-2 py-0.5 text-[11px] text-zinc-400" key={flag}>
                        {flag}
                    </span>
                ))}
                {snapshot.defectFlags.length > 4 && (
                    <span className="rounded border border-zinc-700 bg-zinc-900 px-2 py-0.5 text-[11px] text-zinc-400">
                        +{String(snapshot.defectFlags.length - 4)} more
                    </span>
                )}
            </div>
        )}
    </button>
);

const EventCard = ({ event, onFocus }: { event: ChartPatternResolvedEvent; onFocus: (occurrenceId: number) => void }) => (
    <button
        className="min-w-[260px] rounded border border-zinc-800 bg-zinc-950 p-3 text-left transition-colors hover:border-zinc-600"
        type="button"
        onClick={() => onFocus(event.occurrenceId)}
    >
        <div className="flex items-center justify-between gap-3">
            <div className="text-sm font-medium text-zinc-100">{formatPatternLabel(event.patternKey)}</div>
            <div className="text-[11px] text-zinc-500">{formatShortDateTime(event.eventDateTime)}</div>
        </div>

        <div className="mt-2 flex flex-wrap items-center gap-2">
            <span className={`rounded border px-2 py-0.5 text-[11px] ${getDirectionBadgeClass(event.direction)}`}>
                {formatDirectionLabel(event.direction)}
            </span>
            <span className="rounded border border-zinc-700 bg-zinc-900 px-2 py-0.5 text-[11px] text-zinc-300">{event.eventCategory}</span>
            {event.strictnessTier && (
                <span className="rounded border border-zinc-700 bg-zinc-900 px-2 py-0.5 text-[11px] text-zinc-300">
                    {event.strictnessTier}
                </span>
            )}
        </div>

        <div className="mt-3 space-y-1 text-xs text-zinc-400">
            <div>
                Event: <span className="text-zinc-200">{event.eventKey}</span>
            </div>
            <div>
                Phase: <span className="text-zinc-200">{event.phaseKey ?? 'n/a'}</span>
            </div>
            <div>
                Quality: <span className="text-zinc-200">{formatMetricNumber(event.qualityScore)}</span>
            </div>
        </div>
    </button>
);

const OccurrenceCard = ({
    occurrence,
    isFocused,
    onFocus
}: {
    occurrence: ChartPatternResolvedOccurrence;
    isFocused: boolean;
    onFocus: (occurrenceId: number) => void;
}) => (
    <button
        className={`rounded border p-3 text-left transition-colors ${
            isFocused ? 'border-blue-500 bg-blue-500/10' : 'border-zinc-800 bg-zinc-950 hover:border-zinc-600'
        }`}
        type="button"
        onClick={() => onFocus(occurrence.occurrenceId)}
    >
        <div className="flex flex-wrap items-center gap-2">
            <span className="text-sm font-semibold text-zinc-100">{formatPatternLabel(occurrence.patternKey)}</span>
            <span className={`rounded border px-2 py-0.5 text-[11px] ${getDirectionBadgeClass(occurrence.direction)}`}>
                {formatDirectionLabel(occurrence.direction)}
            </span>
            <span className={`rounded border px-2 py-0.5 text-[11px] ${getStateBadgeClass(occurrence.state)}`}>{occurrence.state}</span>
            {occurrence.strictnessTier && (
                <span className="rounded border border-zinc-700 bg-zinc-900 px-2 py-0.5 text-[11px] text-zinc-300">
                    {occurrence.strictnessTier}
                </span>
            )}
        </div>

        <div className="mt-3 grid grid-cols-2 gap-3 text-xs text-zinc-400 md:grid-cols-4">
            <div>
                <div className="uppercase tracking-[0.12em] text-zinc-500">Formed</div>
                <div className="mt-1 text-zinc-200">
                    {occurrence.formedDateTime ? formatShortDateTime(occurrence.formedDateTime) : 'n/a'}
                </div>
            </div>
            <div>
                <div className="uppercase tracking-[0.12em] text-zinc-500">Triggered</div>
                <div className="mt-1 text-zinc-200">
                    {occurrence.triggeredDateTime ? formatShortDateTime(occurrence.triggeredDateTime) : 'n/a'}
                </div>
            </div>
            <div>
                <div className="uppercase tracking-[0.12em] text-zinc-500">Phase</div>
                <div className="mt-1 text-zinc-200">{occurrence.phaseKey ?? 'n/a'}</div>
            </div>
            <div>
                <div className="uppercase tracking-[0.12em] text-zinc-500">Quality</div>
                <div className="mt-1 text-zinc-200">{formatMetricNumber(occurrence.qualityScore)}</div>
            </div>
            <div>
                <div className="uppercase tracking-[0.12em] text-zinc-500">Last Event</div>
                <div className="mt-1 text-zinc-200">{occurrence.lastEventKey ?? occurrence.lastEventCategory ?? 'n/a'}</div>
            </div>
            <div>
                <div className="uppercase tracking-[0.12em] text-zinc-500">{getConfirmationLabel(occurrence.direction)}</div>
                <div className="mt-1 text-zinc-200">{formatMetricNumber(occurrence.breakoutPrice)}</div>
            </div>
            <div>
                <div className="uppercase tracking-[0.12em] text-zinc-500">Stop</div>
                <div className="mt-1 text-zinc-200">{formatMetricNumber(occurrence.stopPrice)}</div>
            </div>
            <div>
                <div className="uppercase tracking-[0.12em] text-zinc-500">Target</div>
                <div className="mt-1 text-zinc-200">{formatMetricNumber(occurrence.target1Price)}</div>
            </div>
        </div>
    </button>
);

const ChartPatternPanel = ({
    chartPatternDisplayMode,
    chartPatternKeyFilter,
    duration,
    focusedChartPatternOccurrenceId,
    historicalEvents,
    historicalOccurrences,
    isLoading,
    onChartPatternKeyFilterChange,
    onFocusOccurrence,
    onShowHistoricalOccurrences,
    onShowPitReplay,
    onShowAllCurrent,
    onShowSelected,
    summary
}: ChartPatternPanelProps) => {
    const availablePatternKeys = useMemo(() => {
        const keys = new Set([
            ...(summary?.snapshots ?? []).map((snapshot) => snapshot.patternKey),
            ...historicalOccurrences.map((occurrence) => occurrence.patternKey)
        ]);

        return [...keys].sort((left, right) => formatPatternLabel(left).localeCompare(formatPatternLabel(right)));
    }, [historicalOccurrences, summary]);

    const filteredSummarySnapshots = useMemo(
        () =>
            (summary?.snapshots ?? []).filter((snapshot) => (chartPatternKeyFilter ? snapshot.patternKey === chartPatternKeyFilter : true)),
        [chartPatternKeyFilter, summary]
    );

    const filteredCounts = useMemo(() => buildFilteredSummaryCounts(filteredSummarySnapshots), [filteredSummarySnapshots]);

    const filteredHistoricalOccurrences = useMemo(
        () =>
            getChartPatternHistoricalOccurrencesForDisplay({
                occurrences: historicalOccurrences,
                duration,
                chartPatternKeyFilter
            }),
        [chartPatternKeyFilter, duration, historicalOccurrences]
    );

    const historicalCounts = useMemo(() => buildFilteredOccurrenceCounts(filteredHistoricalOccurrences), [filteredHistoricalOccurrences]);

    const { snapshotsToRender, selectedOccurrenceIds } = useMemo(
        () =>
            getChartPatternSnapshotsForDisplay({
                summary,
                snapshots: summary?.snapshots ?? [],
                duration,
                chartPatternDisplayMode,
                chartPatternKeyFilter,
                focusedChartPatternOccurrenceId
            }),
        [summary, duration, chartPatternDisplayMode, chartPatternKeyFilter, focusedChartPatternOccurrenceId]
    );

    const currentModeEventsToRender = useMemo(
        () =>
            getChartPatternEventsForDisplay({
                summary,
                duration,
                chartPatternKeyFilter,
                occurrenceIds: new Set(snapshotsToRender.map((snapshot) => snapshot.occurrenceId))
            }).slice(0, 12),
        [summary, duration, chartPatternKeyFilter, snapshotsToRender]
    );

    const historicalEventsToRender = useMemo(
        () =>
            getChartPatternHistoricalEventsForDisplay({
                events: historicalEvents,
                duration,
                chartPatternKeyFilter,
                occurrenceIds: new Set(
                    (chartPatternDisplayMode === 'pitReplay' && focusedChartPatternOccurrenceId != null
                        ? filteredHistoricalOccurrences.filter((occurrence) => occurrence.occurrenceId === focusedChartPatternOccurrenceId)
                        : filteredHistoricalOccurrences
                    ).map((occurrence) => occurrence.occurrenceId)
                )
            }).slice(0, 12),
        [
            chartPatternDisplayMode,
            chartPatternKeyFilter,
            duration,
            filteredHistoricalOccurrences,
            focusedChartPatternOccurrenceId,
            historicalEvents
        ]
    );

    const eventsToRender =
        chartPatternDisplayMode === 'historicalOccurrences' || chartPatternDisplayMode === 'pitReplay'
            ? historicalEventsToRender
            : currentModeEventsToRender;

    let latestSnapshotDateTime: string | null = null;

    if (
        (chartPatternDisplayMode === 'historicalOccurrences' || chartPatternDisplayMode === 'pitReplay') &&
        historicalEventsToRender.length > 0
    ) {
        latestSnapshotDateTime = historicalEventsToRender[0]?.eventDateTime ?? null;
    } else if (filteredSummarySnapshots.length) {
        let latestSnapshot = filteredSummarySnapshots[0] ?? null;

        for (const snapshot of filteredSummarySnapshots) {
            if (!latestSnapshot || getUnixTime(snapshot.snapshotDateTime) > getUnixTime(latestSnapshot.snapshotDateTime)) {
                latestSnapshot = snapshot;
            }
        }

        latestSnapshotDateTime = latestSnapshot?.snapshotDateTime ?? null;
    }

    if (!isLoading && !summary && historicalOccurrences.length === 0) {
        return (
            <div className="rounded border border-zinc-800 bg-[#111111] p-4 text-sm text-zinc-400">
                No chart patterns are available for this window.
            </div>
        );
    }

    return (
        <div className="grid gap-4 xl:grid-cols-[minmax(0,1.15fr)_minmax(340px,0.85fr)]">
            <section className="rounded border border-zinc-800 bg-[#111111] p-4">
                <div className="flex flex-wrap items-start justify-between gap-3">
                    <div>
                        <div className="text-xs uppercase tracking-[0.16em] text-zinc-500">Chart Patterns</div>
                        <div className="mt-1 text-lg font-semibold text-zinc-100">
                            {isLoading
                                ? 'Loading patterns...'
                                : chartPatternDisplayMode === 'historicalOccurrences' || chartPatternDisplayMode === 'pitReplay'
                                  ? `${String(filteredHistoricalOccurrences.length)} historical occurrence(s)`
                                  : `${String(snapshotsToRender.length)} visible pattern(s)`}
                        </div>
                        <div className="mt-1 text-xs text-zinc-500">
                            {chartPatternDisplayMode === 'historicalOccurrences'
                                ? 'Historical mode shows occurrence geometry and milestone events across the visible range. Click an occurrence or event to open PIT replay.'
                                : chartPatternDisplayMode === 'pitReplay'
                                  ? 'PIT replay focuses one occurrence and shows bar-by-bar phase markers where historical PIT snapshots exist.'
                                  : 'Click a pattern or event card to focus and zoom that occurrence on the chart. Focused mode shows per-bar phase markers.'}
                        </div>
                        {latestSnapshotDateTime && (
                            <div className="mt-1 text-xs text-zinc-500">As of {formatShortDateTime(latestSnapshotDateTime)}</div>
                        )}
                    </div>

                    <div className="flex flex-wrap items-center gap-2">
                        <label className="flex items-center gap-2 rounded border border-zinc-700 bg-zinc-950 px-2 py-1 text-xs text-zinc-300">
                            <span className="uppercase tracking-[0.12em] text-zinc-500">Type</span>
                            <select
                                className="bg-transparent text-zinc-100 outline-none"
                                value={chartPatternKeyFilter ?? ''}
                                onChange={(event) => {
                                    const nextPatternKey =
                                        availablePatternKeys.find((patternKey) => patternKey === event.target.value) ?? null;

                                    onChartPatternKeyFilterChange(nextPatternKey);
                                }}
                            >
                                <option value="">All Patterns</option>
                                {availablePatternKeys.map((patternKey) => (
                                    <option key={patternKey} value={patternKey}>
                                        {formatPatternLabel(patternKey)}
                                    </option>
                                ))}
                            </select>
                        </label>
                        <button
                            className={`${MODE_BUTTON_CLASS} ${
                                chartPatternDisplayMode === 'selected'
                                    ? 'border-blue-500 bg-blue-500/10 text-blue-300'
                                    : 'border-zinc-700 bg-zinc-950 text-zinc-400 hover:text-zinc-200'
                            }`}
                            disabled={chartPatternDisplayMode === 'selected'}
                            type="button"
                            onClick={onShowSelected}
                        >
                            Selected
                        </button>
                        <button
                            className={`${MODE_BUTTON_CLASS} ${
                                chartPatternDisplayMode === 'allCurrent'
                                    ? 'border-emerald-500 bg-emerald-500/10 text-emerald-300'
                                    : 'border-zinc-700 bg-zinc-950 text-zinc-400 hover:text-zinc-200'
                            }`}
                            disabled={chartPatternDisplayMode === 'allCurrent'}
                            type="button"
                            onClick={onShowAllCurrent}
                        >
                            All Current
                        </button>
                        <button
                            className={`${MODE_BUTTON_CLASS} ${
                                chartPatternDisplayMode === 'historicalOccurrences'
                                    ? 'border-fuchsia-500 bg-fuchsia-500/10 text-fuchsia-300'
                                    : 'border-zinc-700 bg-zinc-950 text-zinc-400 hover:text-zinc-200'
                            }`}
                            disabled={chartPatternDisplayMode === 'historicalOccurrences'}
                            type="button"
                            onClick={onShowHistoricalOccurrences}
                        >
                            Historical
                        </button>
                        <button
                            className={`${MODE_BUTTON_CLASS} ${
                                chartPatternDisplayMode === 'pitReplay'
                                    ? 'border-cyan-500 bg-cyan-500/10 text-cyan-300'
                                    : 'border-zinc-700 bg-zinc-950 text-zinc-400 hover:text-zinc-200'
                            }`}
                            disabled={chartPatternDisplayMode === 'pitReplay' || focusedChartPatternOccurrenceId == null}
                            type="button"
                            onClick={onShowPitReplay}
                        >
                            PIT Replay
                        </button>
                    </div>
                </div>

                {(chartPatternDisplayMode === 'historicalOccurrences' || chartPatternDisplayMode === 'pitReplay'
                    ? historicalCounts.total > 0
                    : summary?.counts) && (
                    <div className="mt-4">
                        <SummaryCounts
                            counts={
                                chartPatternDisplayMode === 'historicalOccurrences' || chartPatternDisplayMode === 'pitReplay'
                                    ? historicalCounts
                                    : filteredCounts
                            }
                        />
                    </div>
                )}

                <div className="mt-4 space-y-3">
                    {isLoading ? (
                        <div className="rounded border border-zinc-800 bg-zinc-950 p-4 text-sm text-zinc-400">
                            Loading chart-pattern detail...
                        </div>
                    ) : chartPatternDisplayMode === 'historicalOccurrences' || chartPatternDisplayMode === 'pitReplay' ? (
                        filteredHistoricalOccurrences.length === 0 ? (
                            <div className="rounded border border-zinc-800 bg-zinc-950 p-4 text-sm text-zinc-400">
                                No historical occurrences for the current mode.
                            </div>
                        ) : (
                            filteredHistoricalOccurrences.map((occurrence) => (
                                <OccurrenceCard
                                    isFocused={focusedChartPatternOccurrenceId === occurrence.occurrenceId}
                                    key={occurrence.occurrenceId}
                                    occurrence={occurrence}
                                    onFocus={(occurrenceId) => onFocusOccurrence(occurrenceId, 'pitReplay')}
                                />
                            ))
                        )
                    ) : snapshotsToRender.length === 0 ? (
                        <div className="rounded border border-zinc-800 bg-zinc-950 p-4 text-sm text-zinc-400">
                            No visible patterns for the current mode.
                        </div>
                    ) : (
                        snapshotsToRender.map((snapshot) => (
                            <SnapshotCard
                                isFocused={focusedChartPatternOccurrenceId === snapshot.occurrenceId}
                                key={`${snapshot.occurrenceId}-${snapshot.snapshotDateTime}`}
                                snapshot={snapshot}
                                onFocus={(occurrenceId) => onFocusOccurrence(occurrenceId, 'focused')}
                            />
                        ))
                    )}
                </div>
            </section>

            <section className="rounded border border-zinc-800 bg-[#111111] p-4">
                <div className="text-xs uppercase tracking-[0.16em] text-zinc-500">Recent Events</div>
                <div className="mt-1 text-lg font-semibold text-zinc-100">
                    {isLoading ? 'Loading events...' : `${String(eventsToRender.length)} recent event(s)`}
                </div>
                <div className="mt-4 flex gap-3 overflow-x-auto pb-1">
                    {isLoading ? (
                        <div className="rounded border border-zinc-800 bg-zinc-950 p-4 text-sm text-zinc-400">
                            Loading chart-pattern events...
                        </div>
                    ) : eventsToRender.length === 0 ? (
                        <div className="rounded border border-zinc-800 bg-zinc-950 p-4 text-sm text-zinc-400">
                            No recent events for the visible occurrences.
                        </div>
                    ) : (
                        eventsToRender.map((event) => (
                            <EventCard
                                event={event}
                                key={`${event.occurrenceId}-${event.eventKey}-${event.eventDateTime}`}
                                onFocus={(occurrenceId) =>
                                    onFocusOccurrence(
                                        occurrenceId,
                                        chartPatternDisplayMode === 'historicalOccurrences' || chartPatternDisplayMode === 'pitReplay'
                                            ? 'pitReplay'
                                            : 'focused'
                                    )
                                }
                            />
                        ))
                    )}
                </div>

                {!isLoading &&
                    (chartPatternDisplayMode === 'historicalOccurrences' || chartPatternDisplayMode === 'pitReplay'
                        ? filteredHistoricalOccurrences.length > 0
                        : selectedOccurrenceIds.size > 0) && (
                        <div className="mt-4 text-xs text-zinc-500">
                            Event strip is scoped to the currently visible pattern occurrences so the chart stays readable.
                        </div>
                    )}
            </section>
        </div>
    );
};

export default ChartPatternPanel;
